import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Mic as MicIcon,
} from '@mui/icons-material';

interface ToolSearchProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  placeholder?: string;
  suggestions?: string[];
  recentSearches?: string[];
  onSuggestionSelect?: (suggestion: string) => void;
  className?: string;
}

/**
 * Mobile-optimized search interface for dance studio tools
 * Features real-time filtering, suggestions, and voice search support
 */
export const ToolSearch: React.FC<ToolSearchProps> = ({
  searchQuery,
  onSearchChange,
  placeholder = 'Search tools...',
  suggestions = [],
  recentSearches = [],
  onSuggestionSelect,
  className,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      onSearchChange(value);
      setShowSuggestions(value.length > 0 && suggestions.length > 0);
    },
    [onSearchChange, suggestions.length]
  );

  const handleClear = useCallback(() => {
    onSearchChange('');
    setShowSuggestions(false);
  }, [onSearchChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (searchQuery.length > 0 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [searchQuery.length, suggestions.length]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Delay hiding suggestions to allow for suggestion clicks
    setTimeout(() => setShowSuggestions(false), 150);
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      onSearchChange(suggestion);
      setShowSuggestions(false);
      onSuggestionSelect?.(suggestion);
    },
    [onSearchChange, onSuggestionSelect]
  );

  const handleVoiceSearch = useCallback(() => {
    // Check if speech recognition is available
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        const { transcript } = event.results[0][0];
        onSearchChange(transcript);
      };

      recognition.onerror = (event: any) => {
        console.warn('Speech recognition error:', event.error);
      };

      recognition.start();
    }
  }, [onSearchChange]);

  const isVoiceSearchAvailable =
    'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

  const filteredSuggestions = suggestions.filter(suggestion =>
    suggestion.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box className={className} sx={{ position: 'relative', mb: 2 }}>
      {/* Search Input */}
      <TextField
        fullWidth
        value={searchQuery}
        onChange={handleSearchChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: 'background.paper',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
                borderWidth: 2,
              },
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem',
            py: 1.5,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  color: isFocused ? 'primary.main' : 'text.secondary',
                  fontSize: 20,
                }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Voice Search Button */}
                {isVoiceSearchAvailable && (
                  <IconButton
                    size="small"
                    onClick={handleVoiceSearch}
                    sx={{
                      color: 'text.secondary',
                      minWidth: 32,
                      minHeight: 32,
                      mr: 0.5,
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                    aria-label="Voice search"
                  >
                    <MicIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}

                {/* Clear Button */}
                {searchQuery && (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    sx={{
                      color: 'text.secondary',
                      minWidth: 32,
                      minHeight: 32,
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                    aria-label="Clear search"
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
            </InputAdornment>
          ),
        }}
      />

      {/* Search Suggestions */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
            mt: 0.5,
            maxHeight: 200,
            overflow: 'auto',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              color: 'text.secondary',
              fontWeight: 500,
              display: 'block',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            Suggestions
          </Typography>

          {filteredSuggestions.map(suggestion => (
            <Box
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              sx={{
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                minHeight: 44, // Touch target minimum
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&:last-child': {
                  borderRadius: '0 0 4px 4px',
                },
              }}
            >
              <SearchIcon
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  mr: 1.5,
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {suggestion}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Recent Searches */}
      {!searchQuery && recentSearches.length > 0 && isFocused && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              mb: 1,
              display: 'block',
            }}
          >
            Recent Searches
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {recentSearches.slice(0, 5).map(search => (
              <Chip
                key={search}
                label={search}
                size="small"
                variant="outlined"
                onClick={() => handleSuggestionClick(search)}
                sx={{
                  fontSize: '0.75rem',
                  height: 28,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ToolSearch;
